import { cellToLatLng } from "h3-js";
import { VERSION } from "../../version.js";
import { hairlineRow, button, modal, collapsible, editorialStatusBlock } from "../../design/components.js";
import { LAUNCH_LOCALES, type Locale } from "../../i18n/catalog.js";
import { VOICE_STOPS, DEFAULT_TONE, stopIndex, valueForIndex, type ToneId } from "../../i18n/tone/index.js";
import { homeScreen } from "../onboarding/home.js";
import { flagsAndAppeals } from "./transparency.js";
import { safetyDisclosure } from "./safety.js";
import { getBeaconCard } from "../install/install-modal.js";
import type { LabelAction } from "@beacon/client-core";
import type { HomePlace, SavedPlace, Settings } from "../../platform/store.js";
import { BUCKETS, defaultBucketLevel, type Bucket } from "../now/display-bucket.js";
import { AUTO_DISMISS_OPTIONS, DEFAULT_AUTO_DISMISS_MS } from "../../sw/notif-config.js";
import type { RawDepth } from "../now/raw-report-model.js";
import type { Standing } from "../../platform/standing-transport.js";

type Tr = (k: string, p?: Record<string, string | number>) => string;

export interface YouTabCtx {
  t: Tr;
  home?: HomePlace;
  locale: Locale;
  settings: Settings;
  consents: string[];
  /** Actions against this identity (trust.label fold) — renders only when non-empty (E2). */
  actions: LabelAction[];
  /** Open the appeal sheet for a label action (moderation spec §7). */
  onAppeal: (action: LabelAction) => void;
  /** Wire a freshly-opened modal into the app's back-button stack (Android/browser back
   *  closes it). Given the modal's raw teardown, returns the close() to use as onClose. */
  onModalBack?: (close: () => void) => () => void;
  /** Humanized reporter standing from the corroboration service — undefined when
   *  unavailable (trust card falls back to its static tier hairline). */
  standing?: Standing;
  /** Seed cell for the home picker when no home is set yet (browse-only). */
  defaultCell: string;
  onSetHome: (h: { cell: string; radiusKm: number; placeName?: string }) => void;
  onSetRadius: (km: number) => void;
  onSetQuietHours: (q: { startHour: number; endHour: number } | undefined) => void;
  onSetAlertLevel: (bucket: string, level: string) => void;
  onToggleConsent: (purpose: string, grant: boolean) => void;
  onToggleNotifications: (on: boolean) => void;
  /** Set how long a non-critical OS notification stays before it self-closes (ms; 0 = sticky). */
  onSetNotifyAutoDismiss: (ms: number) => void;
  onSetLocale: (l: Locale) => void;
  /** Set the Voice tone overlay. */
  onSetTone: (tone: ToneId) => void;
  /** Toggle the opt-in foreground alert sound. */
  onSetAlertSound: (on: boolean) => void;
  /** Play a one-shot sample of the current voice's alert sound (also unlocks audio). */
  onPreviewAlertSound: () => void;
  onExportRecovery: () => void;
  onImportRecovery: () => void;
  onRotateKeys: () => void;
  onExportData: () => void;
  onErase: () => void;
  /** Saved places (home excluded). */
  places: SavedPlace[];
  onSetLiveFollow: (v: { enabled: boolean; radiusKm: number }) => void;
  /** Toggle keep-screen-on during live-follow (default on; opting out lets the device sleep). */
  onSetKeepAwake: (on: boolean) => void;
  /** Enable/configure the raw-report map layer (power-user gate). */
  onSetRawReports: (next: { enabled: boolean; depth: RawDepth }) => void;
  /** Toggle the authoritative spread-cone H3-cell debug overlay. */
  onSetConeCells: (next: { enabled: boolean }) => void;
  /** Edit a saved place (upsert by id): name / radius / relocated cell. */
  onEditPlace: (place: { id: string; cell: string; radiusKm: number; placeName?: string }) => void;
  /** Move a saved place up/down in the zone order. */
  onReorderPlace: (id: string, dir: "up" | "down") => void;
  liveFollowUnavailable?: boolean;
  onAddPlace: (p: { cell: string; radiusKm: number; placeName?: string }) => void;
  /** JIT geolocation → res-8 cell (null if denied). Enables "Use current location" next to
   *  "Add a place": opens the picker centred on the fix. */
  geolocate?: () => Promise<string | null>;
  onRemovePlace: (id: string) => void;
  /** Opens the "Get Beacon on your phone" modal. Provided only in a browser
   *  context (never inside the native app or an installed PWA) — see plans/M3. */
  onGetApp?: () => void;
  /** This identity's DID — shown as a Beacon ID fingerprint in the field card. */
  did?: string;
}

const PURPOSES = ["alerts", "helperAvailability", "preciseLocationRelease", "publicMetrics"] as const;
const ALERT_LEVELS = ["off", "info", "warning", "critical"] as const;

function el(tag: string, cls?: string, text?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

function card(title: string): HTMLElement {
  const c = el("section", "you-card");
  c.append(el("div", "you-card__title bx-microlabel", title));
  return c;
}

function note(t: Tr, key: string): HTMLElement {
  return el("p", "you-note bx-body", t(key));
}

const SVGNS = "http://www.w3.org/2000/svg";
const GLYPHS: Record<"up" | "down" | "edit" | "trash" | "check" | "x", string[]> = {
  up: ["M4 12 L10 6 L16 12"],
  down: ["M4 8 L10 14 L16 8"],
  edit: ["M12.5 4.5 L15.5 7.5 L7 15.5 L4 16 L4.5 13 Z"],
  trash: ["M4 6 H16", "M8 6 V4.5 H12 V6", "M6 6 L6.8 16.5 H13.2 L14 6"],
  check: ["M4 10.5 L8.5 15 L16 5.5"],
  x: ["M5 5 L15 15", "M15 5 L5 15"],
};

/** Compact icon-only row action (saved-place up/down/edit/remove). Named via
 *  aria-label/title so it stays accessible without a text label taking width. */
function glyphButton(kind: keyof typeof GLYPHS, label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "you-place__act";
  b.setAttribute("aria-label", label);
  b.title = label;
  const svg = document.createElementNS(SVGNS, "svg");
  for (const [k, v] of Object.entries({
    viewBox: "0 0 20 20", width: "18", height: "18", fill: "none", stroke: "currentColor",
    "stroke-width": "1.6", "stroke-linecap": "round", "stroke-linejoin": "round", "aria-hidden": "true",
  })) svg.setAttribute(k, v);
  for (const d of GLYPHS[kind]) {
    const p = document.createElementNS(SVGNS, "path");
    p.setAttribute("d", d);
    svg.append(p);
  }
  b.append(svg);
  b.addEventListener("click", onClick);
  return b;
}

/** Humanized standing copy keys for the Trust card. Empty when standing is
 *  unavailable (card keeps its static content). No raw tier letters.
 *  Exported for unit testing — the only external caller is standing.test.ts. */
export function standingLines(t: Tr, s?: Standing): string[] {
  if (!s) return [];
  if (s.capability === "direct") return [t("you.trust.capability.direct")];
  const hasGaps =
    s.progress.confirmedRemaining > 0 ||
    s.progress.ageDaysRemaining > 0 ||
    s.progress.presenceDaysRemaining > 0;
  if (!hasGaps) return [t("you.trust.capability.full")];
  const out = [t("you.trust.capability.needsConfirm")];
  if (s.progress.confirmedRemaining > 0) out.push(t("you.trust.progress.confirmed", { confirmed: s.progress.confirmedRemaining }));
  // age (calendar days since signup) and presence (distinct active days/30) are
  // different units; surface the larger as the single "keep using Beacon" hint.
  // presenceDaysRemaining is folded into this same days line.
  const days = Math.max(s.progress.ageDaysRemaining, s.progress.presenceDaysRemaining);
  if (days > 0) out.push(t("you.trust.progress.days", { days }));
  return out;
}

/** Which standing headline the field card shows. "full" (no remaining gaps) and
 *  "direct" both read as trusted; only an active climb reads as becoming-trusted. */
function cardHeadlineKey(s?: Standing): string {
  if (!s) return "you.card.neighbor";
  if (s.capability === "direct") return "you.card.trusted";
  const gaps =
    s.progress.confirmedRemaining > 0 || s.progress.ageDaysRemaining > 0 || s.progress.presenceDaysRemaining > 0;
  return gaps ? "you.card.building" : "you.card.trusted";
}

/** Signature header: a stamped "field card" summarizing who you are in the network —
 *  brand, your standing as a neighbor, your home watch, and your Beacon ID.
 *  Replaces the old editorial title. */
export function fieldCard(o: {
  t: Tr; home?: HomePlace; standing?: Standing; did?: string;
}): HTMLElement {
  const { t } = o;
  const root = el("section", "you-fieldcard");

  const top = el("div", "you-fieldcard__top");
  const brand = el("div", "you-fieldcard__brand");
  brand.append(el("span", "you-fieldcard__mark", "▲"), el("span", "you-fieldcard__word bx-display", "Beacon"));
  top.append(brand);

  const standing = el("h2", "you-fieldcard__standing bx-display", t(cardHeadlineKey(o.standing)));

  const homeLine = el("div", "you-fieldcard__home bx-body");
  homeLine.textContent = o.home
    ? `${o.home.placeName ?? t("you.loc.home")} · ◎ ${t("you.card.watch", { n: o.home.radiusKm })}`
    : t("you.card.noHome");

  root.append(top, standing, homeLine);

  if (o.did) {
    const id = el("div", "you-fieldcard__id bx-microlabel", o.did.slice(-8));
    id.setAttribute("data-beacon-id", "");
    id.setAttribute("aria-label", t("you.card.id"));
    root.append(id);
  }
  return root;
}

/** Which half of the settings surface to render: "you" = about the person (field card,
 *  home & places incl. live position, voice & sound, language, flags/appeals); "beacon" =
 *  app behaviour (alerts & notifications, trust, privacy, app, raw reports, cone debug). */
export type SettingsPart = "you" | "beacon";

/** Beacon tab (icon-only △): app-behaviour settings — the other half of youTab. */
export function beaconTab(ctx: YouTabCtx): HTMLElement { return youTab(ctx, "beacon"); }

/** You tab (GUI §5): plan badge + grouped hairline cards over the real privacy/identity surface. */
export function youTab(ctx: YouTabCtx, part: SettingsPart = "you"): HTMLElement {
  const t = ctx.t;
  const you = part === "you";
  const root = el("section", `screen you-tab${you ? "" : " beacon-tab"}`);

  // Signature header ("Trusted neighbor" …) — stays the You tab's heading.
  if (you) root.append(fieldCard({ t, home: ctx.home, standing: ctx.standing, did: ctx.did }));
  else root.append(editorialStatusBlock({ microlabel: t("beacon.tab.step"), headline: t("beacon.tab.title") }));

  // --- Location & alerts ---------------------------------------------------
  // === Home & places — the surface configured most; kept fully visible. =====
  const homeCard = card(t("you.loc.homeTitle"));
  if (ctx.home) {
    // Home name (editable) — names distinguish zones in the Now watched list/alerts.
    const homeNameRow = el("label", "you-field");
    homeNameRow.append(el("span", "bx-body", t("you.loc.homeName")));
    const homeName = document.createElement("input");
    homeName.type = "text";
    homeName.className = "bx-input";
    homeName.setAttribute("data-home-name", "");
    homeName.setAttribute("aria-label", t("you.loc.homeName"));
    homeName.value = ctx.home.placeName ?? "";
    homeName.addEventListener("change", () => ctx.onSetHome({ ...ctx.home!, placeName: homeName.value || undefined }));
    homeNameRow.append(homeName);
    homeCard.append(homeNameRow);

    // Watch radius (quick inline adjust).
    const radiusRow = el("label", "you-field");
    radiusRow.append(el("span", "bx-body", t("you.loc.radius")));
    const radius = document.createElement("input");
    radius.type = "range"; radius.min = "5"; radius.max = "50"; radius.value = String(ctx.home.radiusKm);
    radius.setAttribute("data-radius", "");
    radius.setAttribute("aria-label", t("you.loc.radius"));
    const radiusVal = el("span", "bx-microlabel", t("you.loc.km", { n: ctx.home.radiusKm }));
    radius.addEventListener("input", () => { radiusVal.textContent = t("you.loc.km", { n: Number(radius.value) }); });
    radius.addEventListener("change", () => ctx.onSetRadius(Number(radius.value)));
    radiusRow.append(radius, radiusVal);
    homeCard.append(radiusRow);

    // Coordinates + Change — the relocate action sits next to the coords (home is a
    // place too; this mirrors a saved place's inline edit affordance).
    const [hlat, hlng] = cellToLatLng(ctx.home.cell);
    const coordsRow = el("div", "you-field you-place-row");
    coordsRow.append(el("span", "bx-body", t("you.loc.coords")));
    const coordsActions = el("span", "you-place-row__actions");
    const coordsVal = el("span", "bx-microlabel", `${hlat.toFixed(4)}, ${hlng.toFixed(4)}`);
    coordsVal.setAttribute("data-coords", "");
    const change = button({ label: t("you.loc.change"), variant: "secondary" });
    change.classList.add("you-inline-btn");
    change.setAttribute("data-action", "change-home");
    change.addEventListener("click", () => {
      if (root.querySelector("[data-sheet=set-home]")) return;
      root.append(setHomeSheet(ctx));
    });
    coordsActions.append(coordsVal, change);
    coordsRow.append(coordsActions);
    homeCard.append(coordsRow);
  } else {
    // Browse-only users skipped picking a home in onboarding — let them opt in here.
    const setHome = button({ label: t("you.loc.setHome"), variant: "secondary" });
    setHome.setAttribute("data-action", "set-home");
    setHome.addEventListener("click", () => {
      if (root.querySelector("[data-sheet=set-home]")) return;
      root.append(setHomeSheet(ctx));
    });
    homeCard.append(setHome, note(t, "you.loc.setHomeHint"));
  }

  // Saved places — visible (configured often), same row pattern as home.
  const places = el("div", "you-places");
  places.append(el("div", "bx-microlabel", t("you.loc.places")));
  for (let i = 0; i < ctx.places.length; i++) {
    const p = ctx.places[i];
    // Custom row (not hairlineRow): the name must be able to truncate so long
    // place names never widen the row into a horizontal scroll. Actions are
    // fixed-width glyph buttons.
    const row = el("div", "you-place");
    row.setAttribute("data-place", p.id);
    const name = el("span", "you-place__name bx-body", p.placeName ?? t("you.loc.placeUnnamed"));
    const km = el("span", "you-place__km bx-microlabel", t("you.loc.km", { n: p.radiusKm }));
    // Actions swap to an inline confirm on remove — a small trash glyph next to
    // Edit is easy to mis-tap, so deletion takes a deliberate second tap. The
    // cancel (✗) sits where the trash was, so a stray double-tap cancels.
    const acts = el("span", "you-place__acts");
    const showConfirm = () => {
      acts.replaceChildren();
      const yes = glyphButton("check", t("you.loc.removePlace"), () => ctx.onRemovePlace(p.id));
      yes.classList.add("you-place__act--danger");
      yes.setAttribute("data-confirm-remove", p.id);
      const no = glyphButton("x", t("you.loc.cancel"), () => showActions());
      no.setAttribute("data-cancel-remove", p.id);
      acts.append(el("span", "you-place__confirm bx-microlabel", t("you.loc.removeConfirm")), yes, no);
    };
    const showActions = () => {
      acts.replaceChildren();
      const up = glyphButton("up", t("you.loc.moveUp"), () => ctx.onReorderPlace(p.id, "up"));
      up.setAttribute("data-move-up", p.id);
      const down = glyphButton("down", t("you.loc.moveDown"), () => ctx.onReorderPlace(p.id, "down"));
      down.setAttribute("data-move-down", p.id);
      const edit = glyphButton("edit", t("you.loc.editPlace"), () => {
        if (root.querySelector("[data-sheet=edit-place]")) return;
        root.append(editPlaceSheet(ctx, p));
      });
      edit.setAttribute("data-edit-place", p.id);
      const rm = glyphButton("trash", t("you.loc.removePlace"), () => showConfirm());
      rm.setAttribute("data-remove-place", p.id);
      acts.append(up, down, edit, rm);
    };
    showActions();
    row.append(name, km, acts);
    places.append(row);
  }
  const add = button({ label: t("you.loc.addPlace"), variant: "secondary" });
  add.setAttribute("data-action", "add-place");
  add.addEventListener("click", () => {
    if (root.querySelector("[data-sheet=add-place]")) return;
    root.append(addPlaceSheet(ctx));
  });
  places.append(add);
  if (ctx.geolocate) {
    const geolocate = ctx.geolocate;
    const addHere = button({ label: t("you.loc.addCurrent"), variant: "secondary" });
    addHere.setAttribute("data-action", "add-current-place");
    addHere.addEventListener("click", () => {
      if (root.querySelector("[data-sheet=add-place]")) return;
      (addHere as HTMLButtonElement).disabled = true;
      void geolocate().then((cell) => {
        (addHere as HTMLButtonElement).disabled = false;
        if (root.querySelector("[data-sheet=add-place]")) return;
        root.append(addPlaceSheet(ctx, cell ?? undefined)); // no fix → the plain picker
      });
    });
    places.append(addHere);
  }
  homeCard.append(places);
  if (you) root.append(homeCard);

  // === Alerts — uniform on/off switches; only the per-hazard matrix collapses. ==
  const alerts = card(t("you.loc.alertsTitle"));

  if (ctx.home) {
    // Live position (travel mode) — frequently toggled, so it stays visible.
    const liveRadiusKm = ctx.settings.liveFollow?.radiusKm ?? 5;
    const liveRow = el("label", "you-field you-toggle");
    liveRow.append(el("span", "bx-body", t("you.loc.liveFollow")));
    const liveBox = document.createElement("input");
    liveBox.type = "checkbox";
    liveBox.setAttribute("data-livefollow", "");
    liveBox.checked = !!ctx.settings.liveFollow?.enabled;
    liveRow.append(liveBox);

    const travelRow = el("label", "you-field");
    travelRow.append(el("span", "bx-body", t("you.loc.travelRadius")));
    const travel = document.createElement("input");
    travel.type = "range"; travel.min = "2"; travel.max = "20"; travel.value = String(liveRadiusKm);
    travel.setAttribute("data-travel-radius", "");
    travel.setAttribute("aria-label", t("you.loc.travelRadius"));
    const travelVal = el("span", "bx-microlabel", t("you.loc.km", { n: liveRadiusKm }));
    travel.addEventListener("input", () => { travelVal.textContent = t("you.loc.km", { n: Number(travel.value) }); });
    // Both controls report the CURRENT slider value + checkbox state at fire time.
    liveBox.addEventListener("change", () => ctx.onSetLiveFollow({ enabled: liveBox.checked, radiusKm: Number(travel.value) }));
    travel.addEventListener("change", () => ctx.onSetLiveFollow({ enabled: liveBox.checked, radiusKm: Number(travel.value) }));
    travelRow.append(travel, travelVal);

    // Keep-screen-on while following (default on). A sleeping web app cannot warn.
    const keepRow = el("label", "you-field you-toggle");
    keepRow.append(el("span", "bx-body", t("you.loc.keepAwake")));
    const keepBox = document.createElement("input");
    keepBox.type = "checkbox";
    keepBox.setAttribute("data-keepawake", "");
    keepBox.checked = ctx.settings.keepAwake !== false; // undefined ⇒ on
    keepBox.addEventListener("change", () => ctx.onSetKeepAwake(keepBox.checked));
    keepRow.append(keepBox);

    // Live position is about where YOU are → lives with Home & places (You tab).
    homeCard.append(liveRow, note(t, "you.loc.liveFollowHint"), travelRow, keepRow, note(t, "you.loc.keepAwakeHint"));
    if (ctx.liveFollowUnavailable) {
      const unavailNote = el("p", "you-note bx-body", t("you.loc.liveFollowUnavailable"));
      unavailNote.setAttribute("data-livefollow-unavailable", "");
      homeCard.append(unavailNote);
    }
  }

  // Notifications — uniform one-line switch (moved out of App).
  const notifRow = el("label", "you-field you-toggle");
  notifRow.append(el("span", "bx-body", t("you.app.notifications")));
  const notifBox = document.createElement("input");
  notifBox.type = "checkbox";
  notifBox.setAttribute("data-action", "toggle-notifications");
  notifBox.checked = !!ctx.settings.notificationsEnabled;
  notifBox.addEventListener("change", () => ctx.onToggleNotifications(notifBox.checked));
  notifRow.append(notifBox);
  alerts.append(notifRow);

  // Alert sound — opt-in foreground earcons (default off). Flipping ON plays a sample
  // in the current voice, which also satisfies the browser's audio-unlock gesture. A div
  // (not a label) so the embedded sample button never toggles the checkbox.
  const soundRow = el("div", "you-field you-toggle");
  soundRow.append(el("span", "bx-body", t("you.sound.title")));
  const soundActions = el("span", "you-toggle__actions");
  const soundSample = button({ label: t("you.sound.preview"), variant: "secondary" });
  soundSample.setAttribute("data-action", "preview-alert-sound");
  soundSample.addEventListener("click", () => ctx.onPreviewAlertSound());
  const soundBox = document.createElement("input");
  soundBox.type = "checkbox";
  soundBox.setAttribute("data-action", "toggle-alert-sound");
  soundBox.setAttribute("aria-label", t("you.sound.title"));
  soundBox.checked = !!ctx.settings.alertSoundEnabled;
  soundBox.addEventListener("change", () => {
    if (soundBox.checked) ctx.onPreviewAlertSound();
    ctx.onSetAlertSound(soundBox.checked);
  });
  soundActions.append(soundSample, soundBox);
  soundRow.append(soundActions);
  const soundBlock = [soundRow, note(t, "you.sound.hint")]; // appended to the Voice card (You) below

  // How long a notification stays on screen before it self-dismisses (critical alerts always stay).
  const dismissRow = el("label", "you-field");
  dismissRow.append(el("span", "bx-body", t("you.loc.autoDismiss")));
  const dismissSel = document.createElement("select");
  dismissSel.setAttribute("data-action", "notify-autodismiss");
  dismissSel.setAttribute("aria-label", t("you.loc.autoDismiss"));
  const currentMs = ctx.settings.notifyAutoDismissMs ?? DEFAULT_AUTO_DISMISS_MS;
  for (const ms of AUTO_DISMISS_OPTIONS) {
    const opt = document.createElement("option");
    opt.value = String(ms);
    opt.textContent = ms === 0
      ? t("you.loc.autoDismiss.never")
      : t("you.loc.autoDismiss.seconds", { n: ms / 1000 });
    if (ms === currentMs) opt.selected = true;
    dismissSel.append(opt);
  }
  dismissSel.addEventListener("change", () => ctx.onSetNotifyAutoDismiss(Number(dismissSel.value)));
  dismissRow.append(dismissSel);
  alerts.append(dismissRow);

  // Quiet hours — switch + summary.
  const quietRow = el("label", "you-field you-toggle");
  quietRow.append(el("span", "bx-body", t("you.loc.quiet")));
  const quietActions = el("span", "you-toggle__actions");
  const quiet = document.createElement("input");
  quiet.type = "checkbox";
  quiet.setAttribute("data-quiet", "");
  quiet.checked = !!ctx.settings.quietHours;
  const quietVal = el("span", "bx-microlabel");
  const renderQuiet = () => {
    quietVal.textContent = ctx.settings.quietHours
      ? t("you.loc.quietValue", { start: ctx.settings.quietHours.startHour, end: ctx.settings.quietHours.endHour })
      : t("you.loc.quietOff");
  };
  renderQuiet();
  quiet.addEventListener("change", () => {
    ctx.onSetQuietHours(quiet.checked ? { startHour: 22, endHour: 7 } : undefined);
  });
  quietActions.append(quietVal, quiet);
  quietRow.append(quietActions);
  alerts.append(quietRow);

  // Per-bucket alert level — rarely touched, so it stays behind a disclosure.
  const minsev = el("div", "you-minsev");
  minsev.append(el("div", "bx-microlabel", t("you.loc.minsev")));
  for (const bucket of BUCKETS) {
    const row = el("label", "you-field");
    row.append(el("span", "bx-body", t(`hazard.${bucket}`)));
    const sel = document.createElement("select");
    sel.setAttribute("data-minsev", bucket);
    sel.setAttribute("aria-label", `${t("you.loc.minsev")} — ${t(`hazard.${bucket}`)}`);
    const currentLevel = ctx.settings.alertLevel?.[bucket as Bucket] ?? defaultBucketLevel(bucket);
    for (const lvl of ALERT_LEVELS) {
      const opt = document.createElement("option");
      opt.value = lvl;
      opt.textContent = t(`you.loc.level.${lvl}`);
      if (currentLevel === lvl) opt.selected = true;
      sel.append(opt);
    }
    sel.addEventListener("change", () => ctx.onSetAlertLevel(bucket, sel.value));
    row.append(sel);
    minsev.append(row);
  }
  alerts.append(collapsible(t("you.loc.alertDetail"), minsev));
  if (!you) root.append(alerts);

  // --- Voice — collapsed; a personalization extra, available to everyone ----
  const voiceCard = el("section", "you-card");
  const voiceBody = el("div", "you-voice");
  voiceBody.append(note(t, "you.voice.intro"));
  const currentTone: ToneId = ctx.settings.tone ?? DEFAULT_TONE;
  const tone = document.createElement("input");
  tone.type = "range";
  tone.min = "0";
  tone.max = "100";
  tone.step = "1";
  tone.value = String(valueForIndex(VOICE_STOPS.indexOf(currentTone)));
  tone.setAttribute("data-voice-tone", "");
  tone.setAttribute("aria-label", t("you.voice.title"));
  const stops = el("div", "you-voice__stops bx-microlabel");
  for (const s of VOICE_STOPS) stops.append(el("span", "", t(`you.voice.${s}`)));
  const preview = el("p", "you-voice__preview bx-ugc", t(`you.voice.preview.${currentTone}`));
  // input: snap the thumb to the nearest stop and preview that voice live.
  tone.addEventListener("input", () => {
    const idx = stopIndex(Number(tone.value));
    tone.value = String(valueForIndex(idx));
    preview.textContent = t(`you.voice.preview.${VOICE_STOPS[idx]}`);
  });
  // change: persist + apply.
  tone.addEventListener("change", () => {
    ctx.onSetTone(VOICE_STOPS[stopIndex(Number(tone.value))]);
  });
  voiceBody.append(stops, tone, preview);
  voiceCard.append(...soundBlock, collapsible(t("you.voice.title"), voiceBody));
  if (you) root.append(voiceCard);

  // --- Trust ---------------------------------------------------------------
  const trust = card(t("you.trust.title"));
  const lines = standingLines(t, ctx.standing);
  if (lines.length) {
    for (const text of lines) trust.append(el("p", "you-note bx-body", text));
  } else {
    trust.append(hairlineRow({ leading: t("you.trust.tier"), trailing: t("you.trust.tierUnknown") }));
  }
  // How standing works → disclosure (the factors + vouch a neighbor).
  const how = el("div", "you-disclosure");
  how.append(note(t, "you.trust.how"));
  for (const f of ["age", "confirmed", "vouches"] as const) {
    how.append(hairlineRow({ leading: t(`you.trust.factor.${f}`), trailing: t("you.trust.factorValue") }));
  }
  // Vouch a neighbor (shareable link carries the real did — projector validates vouchers, D2).
  const vouch = button({ label: t("you.trust.vouch"), variant: "secondary" });
  vouch.setAttribute("data-action", "vouch");
  how.append(vouch, note(t, "you.trust.vouchHint"));
  how.append(hairlineRow({ leading: t("you.trust.vouchers"), trailing: t("you.trust.vouchersEmpty") }));
  trust.append(collapsible(t("you.trust.howToggle"), how));

  // Keys & recovery → disclosure (client-core surface, real; rarely touched).
  const keys = el("div", "you-disclosure");
  const exportRecovery = button({ label: t("you.trust.export"), variant: "secondary", onClick: ctx.onExportRecovery });
  exportRecovery.setAttribute("data-action", "export-recovery");
  const importRecovery = button({ label: t("you.trust.import"), variant: "secondary", onClick: ctx.onImportRecovery });
  importRecovery.setAttribute("data-action", "import-recovery");
  const rotate = button({ label: t("you.trust.rotate"), variant: "secondary", onClick: ctx.onRotateKeys });
  rotate.setAttribute("data-action", "rotate-keys");
  keys.append(exportRecovery, note(t, "you.trust.exportHint"), importRecovery, rotate, note(t, "you.trust.rotateHint"));
  trust.append(collapsible(t("you.trust.keysToggle"), keys));
  if (!you) root.append(trust);

  // --- Privacy -------------------------------------------------------------
  const privacy = card(t("you.privacy.title"));
  privacy.append(el("div", "bx-microlabel", t("you.privacy.consentTitle")));
  for (const purpose of PURPOSES) {
    const row = el("label", "you-field you-consent");
    const box = document.createElement("input");
    box.type = "checkbox";
    box.setAttribute("data-consent", purpose);
    box.checked = ctx.consents.includes(purpose);
    box.addEventListener("change", () => ctx.onToggleConsent(purpose, box.checked));
    row.append(box, el("span", "bx-body", t(`you.consent.${purpose}`)));
    privacy.append(row);
  }
  // Your data → disclosure (export + erase). Consents stay visible above.
  const data = el("div", "you-disclosure");
  const exportData = button({ label: t("you.privacy.export"), variant: "secondary", onClick: ctx.onExportData });
  exportData.setAttribute("data-action", "export-data");
  data.append(exportData, note(t, "you.privacy.exportHint"));

  // Erase me — grave confirmation
  const erase = button({ label: t("you.privacy.erase"), variant: "secondary" });
  erase.classList.add("you-erase");
  erase.setAttribute("data-action", "erase");
  erase.addEventListener("click", () => {
    if (root.querySelector("[data-sheet=erase]")) return;
    root.append(eraseSheet(ctx));
  });
  data.append(erase, note(t, "you.privacy.eraseHint"));
  privacy.append(collapsible(t("you.privacy.dataToggle"), data));
  if (!you) root.append(privacy);

  // Safety & privacy disclosure — what the help chat protects + its honest limits.
  if (!you) root.append(safetyDisclosure(t));

  // Funding lives in the commons card (CL-5), appended after this tab by the app —
  // it replaced the old supporter-pledge stub.

  // --- App -----------------------------------------------------------------
  const app = card(t("you.app.title"));
  const langCard = card(t("you.app.language"));
  const langRow = el("label", "you-field");
  langRow.append(el("span", "bx-body", t("you.app.language")));
  const lang = document.createElement("select");
  lang.setAttribute("data-locale", "");
  lang.setAttribute("aria-label", t("you.app.language"));
  for (const l of LAUNCH_LOCALES) {
    const opt = document.createElement("option");
    opt.value = l;
    opt.textContent = l;
    if (l === ctx.locale) opt.selected = true;
    lang.append(opt);
  }
  lang.addEventListener("change", () => ctx.onSetLocale(lang.value as Locale));
  langRow.append(lang);
  langCard.append(langRow);
  if (you) root.append(langCard);

  // Build version (channel label + git describe) — lets you confirm a deploy landed.
  const aboutRow = hairlineRow({ leading: t("you.app.about"), trailing: `${t("you.app.version")} · ${VERSION}` });
  aboutRow.setAttribute("data-testid", "app-version");
  app.append(aboutRow);
  if (!you) root.append(app);

  // --- Advanced: raw-report layer (power-user opt-in) ---------------------
  const rawWrap = el("div", "you-card");
  rawWrap.append(el("h3", "bx-subhead", t("you.raw.title")));
  const rawRow = el("label", "you-field you-toggle");
  rawRow.append(el("span", "bx-body", t("you.raw.toggle")));
  const rawBox = document.createElement("input");
  rawBox.type = "checkbox";
  rawBox.setAttribute("data-raw-reports", "");
  rawBox.checked = !!ctx.settings.rawReports?.enabled;
  rawBox.addEventListener("change", () => {
    if (rawBox.checked) {
      if (!window.confirm(t("you.raw.warning"))) { rawBox.checked = false; return; }
      ctx.onSetRawReports({ enabled: true, depth: ctx.settings.rawReports?.depth ?? "medium" });
    } else {
      ctx.onSetRawReports({ enabled: false, depth: ctx.settings.rawReports?.depth ?? "medium" });
    }
  });
  rawRow.append(rawBox);
  rawWrap.append(rawRow);
  if (ctx.settings.rawReports?.enabled) {
    const depthRow = el("label", "you-field");
    depthRow.append(el("span", "bx-body", t("you.raw.depth.label")));
    const depthSel = document.createElement("select");
    depthSel.setAttribute("data-raw-depth", "");
    depthSel.setAttribute("aria-label", t("you.raw.depth.label"));
    for (const d of ["off", "shallow", "medium", "deep"] as const) {
      const o = document.createElement("option");
      o.value = d; o.textContent = t(`you.raw.depth.${d}`);
      if ((ctx.settings.rawReports?.depth ?? "medium") === d) o.selected = true;
      depthSel.append(o);
    }
    depthSel.addEventListener("change", () => ctx.onSetRawReports({ enabled: true, depth: depthSel.value as RawDepth }));
    depthRow.append(depthSel);
    rawWrap.append(depthRow);
  }
  if (!you) root.append(rawWrap);

  // --- Advanced: spread-cone debug cells (read-only authoritative overlay) -----
  const coneWrap = el("div", "you-card");
  coneWrap.append(el("h3", "bx-subhead", t("you.cone.title")));
  const coneRow = el("label", "you-field you-toggle");
  coneRow.append(el("span", "bx-body", t("you.cone.toggle")));
  const coneBox = document.createElement("input");
  coneBox.type = "checkbox";
  coneBox.setAttribute("data-cone-cells", "");
  coneBox.checked = !!ctx.settings.coneDebugCells?.enabled;
  coneBox.addEventListener("change", () => ctx.onSetConeCells({ enabled: coneBox.checked }));
  coneRow.append(coneBox);
  coneWrap.append(coneRow);
  coneWrap.append(el("p", "you-note bx-body", t("you.cone.helper")));
  if (!you) root.append(coneWrap);

  // --- Get Beacon on your phone (browser context only) --------------------
  if (!you && ctx.onGetApp) root.append(getBeaconCard(t, ctx.onGetApp));

  // --- Flags & appeals (about you; only if non-empty) ------------------------
  if (you && ctx.actions.length > 0) {
    root.append(flagsAndAppeals({ t, actions: ctx.actions, onAppeal: ctx.onAppeal }));
  }

  return root;
}

/** Home picker in a sheet: reuses the onboarding map+radius picker. When a home
 *  already exists it is seeded as the picker's center cell + radius (Change home
 *  area); for a browse-only user it falls back to ctx.defaultCell at radius 15. */
function setHomeSheet(ctx: YouTabCtx): HTMLElement {
  let sheet: HTMLElement;
  const rawClose = (): void => sheet.remove();
  const close = ctx.onModalBack ? ctx.onModalBack(rawClose) : rawClose;
  const picker = homeScreen({
    t: ctx.t,
    defaultCell: ctx.home?.cell ?? ctx.defaultCell,
    initialRadiusKm: ctx.home?.radiusKm,
    onConfirm: (h) => { ctx.onSetHome(h); close(); },
    onBrowse: () => close(),
  });
  sheet = modal({
    title: ctx.t(ctx.home ? "you.loc.change" : "you.loc.setHome"),
    content: picker,
    onClose: close,
    sheetId: "set-home",
  });
  return sheet;
}

/** Add-a-place picker: reuses the onboarding map+radius picker; onConfirm → onAddPlace. */
function addPlaceSheet(ctx: YouTabCtx, seedCell?: string): HTMLElement {
  let sheet: HTMLElement;
  const picker = homeScreen({
    t: ctx.t,
    defaultCell: seedCell ?? ctx.defaultCell,
    compact: true, // the modal already has a title — no redundant STEP 3/3 + giant headline
    onConfirm: (h) => { ctx.onAddPlace({ cell: h.cell, radiusKm: h.radiusKm, placeName: h.placeName }); sheet.remove(); },
    onBrowse: () => sheet.remove(),
  });
  sheet = modal({ title: ctx.t("you.loc.addPlace"), content: picker, onClose: () => sheet.remove(), sheetId: "add-place" });
  return sheet;
}

/** Edit-a-place sheet: a name field + the reused home picker (seeded on this place's
 *  location + radius). The picker's confirm IS the Save — we merge the edited name in. */
function editPlaceSheet(ctx: YouTabCtx, place: { id: string; cell: string; radiusKm: number; placeName?: string }): HTMLElement {
  let sheet: HTMLElement;
  const content = document.createElement("div");
  const nameRow = el("label", "you-field");
  const nameInput = document.createElement("input");
  nameInput.type = "text";
  nameInput.className = "bx-input";
  nameInput.setAttribute("data-place-name", "");
  nameInput.setAttribute("aria-label", ctx.t("you.loc.placeName"));
  nameInput.value = place.placeName ?? "";
  nameRow.append(el("span", "bx-body", ctx.t("you.loc.placeName")), nameInput);
  // Reuse the home picker for location + radius (seeded on this place); its confirm
  // is the Save — we merge the edited name in.
  const picker = homeScreen({
    t: ctx.t,
    defaultCell: place.cell,
    initialRadiusKm: place.radiusKm,
    compact: true, // the modal already has a title — no redundant STEP 3/3 + giant headline
    onConfirm: (h) => { ctx.onEditPlace({ id: place.id, cell: h.cell, radiusKm: h.radiusKm, placeName: nameInput.value || undefined }); sheet.remove(); },
    onBrowse: () => sheet.remove(),
  });
  content.append(nameRow, picker);
  sheet = modal({ title: ctx.t("you.loc.editPlace"), content, onClose: () => sheet.remove(), sheetId: "edit-place" });
  return sheet;
}

/** Grave erase confirmation: the confirm button is gated on typing the exact word. */
function eraseSheet(ctx: YouTabCtx): HTMLElement {
  const t = ctx.t;
  const content = el("div", "you-erase-confirm");
  content.append(el("p", "bx-body", t("you.erase.body")));

  const label = el("label", "you-field");
  label.append(el("span", "bx-microlabel", t("you.erase.confirmLabel")));
  const input = document.createElement("input");
  input.type = "text";
  input.setAttribute("data-erase-input", "");
  input.setAttribute("aria-label", t("you.erase.confirmLabel"));
  label.append(input);
  content.append(label);

  const confirm = button({ label: t("you.erase.confirm"), variant: "primary", onClick: () => ctx.onErase() });
  confirm.setAttribute("data-action", "erase-confirm");
  (confirm as HTMLButtonElement).disabled = true;
  input.addEventListener("input", () => {
    (confirm as HTMLButtonElement).disabled = input.value !== t("you.erase.confirmWord");
  });

  const cancel = button({ label: t("you.erase.cancel"), variant: "secondary" });
  cancel.setAttribute("data-action", "erase-cancel");

  content.append(confirm, cancel);

  let sheet: HTMLElement;
  const rawClose = (): void => sheet.remove();
  const close = ctx.onModalBack ? ctx.onModalBack(rawClose) : rawClose;
  sheet = modal({ title: t("you.erase.title"), content, onClose: close, sheetId: "erase" });
  cancel.addEventListener("click", close);
  return sheet;
}
